import { z } from 'zod';
import { digestSchema, sealContent, verifyContent } from '../hashing/hash.js';
/**
 * One submission attempt, sealed (ADR-013 §3, #208).
 *
 * `StepInstance.submission` is a convenience copy of the LATEST attempt: a resubmission after
 * rejection replaces it, and before this record existed attempt 1's answers survived only if a
 * packet snapshot happened to seal them. A snapshot seals only once nothing in the link's scope
 * is open, so a rejected form was routinely lost. Every attempt now writes one of these, in the
 * same Cosmos transactional batch as the instance replace, so neither exists without the other.
 *
 * Append-only, the same way the packet snapshot is: the id is deterministic per attempt and the
 * write is a Create, so a retry of the same attempt collides instead of writing a second one,
 * and no method exists to update or delete one.
 */
export const SUBMISSION_RECORD_DOC_TYPE = 'submission-record';
/** Deterministic, so the database — not a handler remembering to check — refuses a duplicate. */
export function submissionRecordId(instanceId, attempt) {
    return `submission:${instanceId}:${attempt}`;
}
export const submissionRecordSchema = z.object({
    id: z.string().min(1),
    /** The partition key of the `instances` container, so the record batches with its step. */
    personId: z.string().min(1),
    docType: z.literal(SUBMISSION_RECORD_DOC_TYPE).default(SUBMISSION_RECORD_DOC_TYPE),
    instanceId: z.string().min(1),
    /** Zero-based, as on the instance: the first submission is attempt 0. */
    attempt: z.number().int().nonnegative(),
    /** The step type, so a reader can re-validate the submission without the live instance. */
    type: z.string().min(1),
    /** The pinned definition's digest: which wording this attempt answered. */
    pinnedContentHash: digestSchema,
    /** Exactly as written to the instance. A record, not input, so never re-validated here. */
    submission: z.unknown(),
    /** Digests of the artifacts this attempt names, so the files can be proved unchanged. */
    artifactDigests: z.array(digestSchema),
    submittedAt: z.string().datetime(),
    /** The exact bytes that were hashed, kept so the digest can be re-verified later. */
    canonicalContent: z.string().min(1),
    contentHash: digestSchema,
});
/** The projection that gets sealed. Excludes id, docType and the seal itself. */
function sealable(input) {
    return {
        personId: input.personId,
        instanceId: input.instanceId,
        attempt: input.attempt,
        type: input.type,
        pinnedContentHash: input.pinnedContentHash,
        // JSON by construction: it was parsed from a request body or built by the server.
        submission: (input.submission ?? null),
        artifactDigests: [...input.artifactDigests],
        submittedAt: input.submittedAt,
    };
}
/**
 * Seal the attempt an instance is about to be written as.
 *
 * Built from the instance AS IT WILL BE STORED — its attempt, submission and `submittedAt` —
 * so the record and the convenience copy cannot disagree about what was sent.
 */
export async function buildSubmissionRecord(instance, artifactDigests) {
    if (instance.submittedAt === null)
        throw new Error(`instance ${instance.id}: nothing was submitted to seal`);
    const projection = {
        personId: instance.personId,
        instanceId: instance.id,
        attempt: instance.attempt,
        type: instance.type,
        pinnedContentHash: instance.pinned.contentHash,
        submission: instance.submission,
        artifactDigests: [...artifactDigests],
        submittedAt: instance.submittedAt,
    };
    const sealed = await sealContent(sealable(projection));
    return submissionRecordSchema.parse({
        id: submissionRecordId(instance.id, instance.attempt),
        docType: SUBMISSION_RECORD_DOC_TYPE,
        ...projection,
        canonicalContent: sealed.canonical,
        contentHash: sealed.contentHash,
    });
}
/**
 * Does the stored record still hash to what it claims, and do its fields still produce those
 * bytes? Both, for the reason `verifyPacketSnapshot` gives: the second check is the one that
 * catches an edited field whose canonical bytes were edited to match.
 */
export async function verifySubmissionRecord(record) {
    if (record.id !== submissionRecordId(record.instanceId, record.attempt))
        return false;
    if (!(await verifyContent(record.canonicalContent, record.contentHash)))
        return false;
    const rebuilt = await sealContent(sealable(record));
    return rebuilt.canonical === record.canonicalContent && rebuilt.contentHash === record.contentHash;
}
/**
 * The upload an attempt names, read structurally.
 *
 * Every step type that carries an artifact names it as a top-level `uploadId` on its
 * submission. Reading the field rather than switching on the type keeps the submit paths
 * ignorant of which type they hold.
 */
export function submissionUploadId(submission) {
    if (typeof submission !== 'object' || submission === null)
        return null;
    const uploadId = submission.uploadId;
    return typeof uploadId === 'string' && uploadId.length > 0 ? uploadId : null;
}
//# sourceMappingURL=submission-record.js.map